import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import SimpleDropdown from '../../components/SimpleDropdown';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

interface ContactForm {
  firstName: string;
  lastName: string;
  email: string;
  displayName: string;
  businessPhone: string;
  mobilePhone: string;
  region: string;
  customerId: string | null;
  isPrimary: boolean;
}

interface ContactFormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  displayName?: string;
  businessPhone?: string;
  mobilePhone?: string;
  region?: string;
  customerId?: string;
}

const ContactCreate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const contactsListSearch = (location.state as { contactsListSearch?: string } | null)?.contactsListSearch;
  const contactsListPath =
    contactsListSearch !== undefined ? `/contacts${contactsListSearch}` : '/contacts';
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<ContactForm>({
    firstName: '',
    lastName: '',
    email: '',
    displayName: '',
    businessPhone: '',
    mobilePhone: '',
    region: 'US',
    customerId: null,
    isPrimary: false
  });

  const [errors, setErrors] = useState<ContactFormErrors>({});

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data.customers || [];
    }
  });

  // Create contact mutation
  const createMutation = useMutation({
    mutationFn: async (data: ContactForm) => {
      const response = await api.post('/contacts', data);
      return response.data;
    },
    onSuccess: async (data) => {
      // Invalidate list queries so they refetch when user navigates back
      await queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success(
        <div>
          <p>Contact created successfully!</p>
          <p className="text-sm mt-1">Default password: <code className="bg-gray-100 px-1 py-0.5 rounded">Contact123!</code></p>
          <p className="text-xs mt-1 text-gray-600">User will be prompted to change password on first login</p>
        </div>,
        { duration: 8000 }
      );
      navigate(`/contacts/${data.id}`, { state: location.state });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create contact');
    }
  });

  const validateForm = (): boolean => {
    const newErrors: ContactFormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }

    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }

    // Check if there are any existing inline validation errors
    if (errors.email || errors.businessPhone || errors.mobilePhone) {
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Auto-generate display name if not provided
      const dataToSubmit = {
        ...formData,
        displayName: formData.displayName || `${formData.firstName} ${formData.lastName}`,
        password: 'Contact123!', // Default password for contacts
        isPrimary: formData.isPrimary
      };
      createMutation.mutate(dataToSubmit);
    }
  };

  const validateField = (name: string, value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

    switch (name) {
      case 'email':
        if (value && !emailRegex.test(value)) {
          return 'Invalid email format';
        }
        break;
      case 'businessPhone':
      case 'mobilePhone':
        if (value) {
          const cleanedPhone = value.replace(/\s+/g, '');
          if (!phoneRegex.test(cleanedPhone)) {
            return 'Invalid phone number format';
          }
          // Check minimum 10 digits (excluding country code +)
          const digitCount = cleanedPhone.replace(/[^\d]/g, '').length;
          if (digitCount < 10) {
            return 'Phone number must have at least 10 digits';
          }
        }
        break;
    }
    return '';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Inline validation for email and phone
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(contactsListPath)}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Contacts
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <h1 className="text-xl font-semibold text-gray-900">Create New Contact</h1>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="submit"
                  form="contact-form"
                  disabled={createMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Contact'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(contactsListPath)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} id="contact-form" className="p-8">
            {/* Row 1: First Name and Last Name */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.firstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.lastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Display Name and Email */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      placeholder="Auto-generated if empty"
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Business Phone and Mobile Phone */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Phone
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="tel"
                      name="businessPhone"
                      value={formData.businessPhone}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.businessPhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.businessPhone && (
                      <p className="mt-1 text-xs text-red-600">{errors.businessPhone}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Phone
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="tel"
                      name="mobilePhone"
                      value={formData.mobilePhone}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.mobilePhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.mobilePhone && (
                      <p className="mt-1 text-xs text-red-600">{errors.mobilePhone}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Customer and Primary Contact */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <SimpleDropdown
                      items={customersData || []}
                      value={formData.customerId}
                      onChange={(value) => {
                        setFormData(prev => ({
                          ...prev,
                          customerId: value
                        }));
                        if (errors.customerId) {
                          setErrors(prev => ({ ...prev, customerId: '' }));
                        }
                      }}
                      placeholder="Select a customer"
                      displayField="accountName"
                      valueField="id"
                      error={errors.customerId}
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Primary Contact
                    </label>
                  </div>
                  <div className="col-span-9">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isPrimary}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            isPrimary: e.target.checked
                          }));
                        }}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">
                        Set as primary contact for this customer
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Region */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Region
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      name="region"
                      value={formData.region}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="US">United States</option>
                      <option value="EU">Europe</option>
                      <option value="APAC">Asia Pacific</option>
                      <option value="LATAM">Latin America</option>
                      <option value="MEA">Middle East & Africa</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactCreate;